import React from 'react';
import { useSelector } from 'react-redux';
import { grey, red } from '@mui/material/colors';
import { useFormContext, useWatch } from 'react-hook-form';
import { ListingForm } from 'components/shared/TableListing';
import { ActionsMenu } from 'components/shared/Menu/ActionsMenu';
import { InputField, AutocompleteValueField } from 'components/form';
import { getUserRolesSelectOptions } from 'utils/selectOptionsHelper';
import { capitalize, IconButton, Stack, Typography } from '@mui/material';
import { ArrowDropDown, VisibilityOff, Visibility } from '@mui/icons-material';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { AttributeFieldProps, StagesFormValues } from '../../../../../types';
import { AttributeRequiredNode } from './AttributeRequiredNode';

export const DocumentField = (props: AttributeFieldProps) => {
  const { rowIndex, attributeIndex, prefix, disabled } = props;

  const { control, setValue } = useFormContext<ListingForm<StagesFormValues>>();
  const isPII = useWatch({ control, name: `bodyFields.${rowIndex}.${prefix}.${attributeIndex}.isPII` });

  const users = useSelector((state: any) => state.relatedUsers)
    .filter(({ status, fullName }: any) => status !== 'blocked' && !!fullName)
    .map(({ id, fullName }: any) => ({ value: id, label: fullName! }));

  return (
    <Stack width="100%" gap="0.5rem">
      <InputField
        label="Document Field Name"
        control={control}
        name={`bodyFields.${rowIndex}.${prefix}.${attributeIndex}.name`}
        inputProps={{
          disabled,
          required: true,
          variant: 'outlined',
          placeholder: `${capitalize(prefix)} field name`,
        }}
      />

      <AutocompleteValueField
        multiple
        size="small"
        options={users}
        control={control}
        name={`bodyFields.${rowIndex}.${prefix}.${attributeIndex}.notify`}
        textFieldProps={{
          size: 'small',
          fullWidth: true,
          variant: 'outlined',
          label: 'Notification Recipients',
        }}
      />

      <Stack direction="row" alignItems="center" gap="0.5rem">
        <AttributeRequiredNode {...props} />

        <Stack direction="row" alignItems="center">
          <Stack alignItems="center" justifyContent="center" mr="0.5rem">
            {!isPII ? (
              <Visibility sx={{ fontSize: 16, color: grey[200] }} />
            ) : (
              <VisibilityOff sx={{ fontSize: 16, color: red[500] }} />
            )}
          </Stack>

          <Typography variant="caption">{isPII ? 'Restricted' : 'Visible to everyone'}</Typography>

          <ActionsMenu
            renderTrigger={({ onClick }) => (
              <IconButton onClick={onClick} size="small">
                <ArrowDropDown sx={{ color: 'black' }} fontSize="small" />
              </IconButton>
            )}
            config={[
              {
                disabled,
                color: grey[500],
                label: 'Visible to everyone',
                icon: <Visibility sx={{ fontSize: 16, color: grey[200] }} />,
                action: () => {
                  setValue(`bodyFields.${rowIndex}.${prefix}.${attributeIndex}.isPII`, false);
                  setValue(`bodyFields.${rowIndex}.${prefix}.${attributeIndex}.availableForRoles`, []);
                },
              },
              {
                disabled,
                color: red[500],
                label: 'Restricted',
                icon: <VisibilityOff sx={{ fontSize: 16, color: red[500] }} />,
                action: () => setValue(`bodyFields.${rowIndex}.${prefix}.${attributeIndex}.isPII`, true),
              },
            ]}
          />
        </Stack>
      </Stack>

      <RenderWithCondition condition={isPII}>
        <AutocompleteValueField
          multiple
          size="small"
          control={control}
          options={getUserRolesSelectOptions()}
          name={`bodyFields.${rowIndex}.${prefix}.${attributeIndex}.availableForRoles`}
          textFieldProps={{
            size: 'small',
            fullWidth: true,
            required: true,
            variant: 'outlined',
            label: 'Allowed Roles',
          }}
        />
      </RenderWithCondition>
    </Stack>
  );
};
